import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

interface NotificationsSlotContextValue {
  content: ReactNode;
  count: number;
}

const NotificationsSlotContext = createContext<NotificationsSlotContextValue>({
  content: null,
  count: 0,
});

interface NotificationsSlotProviderProps {
  children: ReactNode;
  content: ReactNode;
  count: number;
}

/**
 * Allows feature providers to inject notification content into the shared header
 * without violating the shared/ → features/ import boundary.
 *
 * Features wrap their subtree with this provider at the router level, passing
 * rendered content and a badge count. The header consumes them via
 * `useNotificationsSlot` without knowing which feature produced them.
 *
 * @example
 * // In a feature provider (router level):
 * <NotificationsSlotProvider content={<InvitationsList />} count={invitations.length}>
 *   {children}
 * </NotificationsSlotProvider>
 */
export function NotificationsSlotProvider({
  children,
  content,
  count,
}: NotificationsSlotProviderProps) {
  return (
    <NotificationsSlotContext.Provider value={{ content, count }}>
      {children}
    </NotificationsSlotContext.Provider>
  );
}

/**
 * Reads notification content and badge count injected by a feature provider.
 *
 * @returns `content` — ReactNode to render in the notifications panel;
 *          `count` — number to display on the bell badge (0 = no badge)
 *
 * @example
 * function HeaderNotifications() {
 *   const { content, count } = useNotificationsSlot()
 *   return (
 *     <Popover>
 *       <PopoverTrigger>
 *         <Bell />
 *         {count > 0 && <Badge>{count}</Badge>}
 *       </PopoverTrigger>
 *       <PopoverContent>{content}</PopoverContent>
 *     </Popover>
 *   )
 * }
 */
export function useNotificationsSlot() {
  return useContext(NotificationsSlotContext);
}
